import { AnalyticsContainer } from 'context/analytics';
import { getCsrfToken } from 'utils/clients/csrf';

export const API_FAILURE_EVENT = 'api-request-failed';

const DEDUPE_WINDOW_MS = 60 * 1000;
const CAP_WINDOW_MS = 10 * 60 * 1000;
const MAX_EVENTS_PER_CAP_WINDOW = 100;
const MAX_MESSAGE_LENGTH = 200;

const lastTrackedAt = new Map<string, number>();
let capWindowStart = 0;
let eventsInCapWindow = 0;

export interface ApiFailureError {
  code?: string;
  message?: string;
  config?: {
    url?: string;
    method?: string;
    headers?: Record<string, unknown>;
    __csrfRetried?: boolean;
  };
  response?: { status?: number; data?: unknown };
}

export const shouldTrackApiFailure = (error: ApiFailureError): boolean => {
  if (error.code === 'ERR_CANCELED') return false;
  const status = error.response?.status;
  return (
    status === undefined || status === 401 || status === 403 || status >= 500
  );
};

export const normalizeEndpoint = (url: string | undefined): string => {
  if (!url) return 'unknown';
  const beUrl = process.env.VITE_APP_BE_URL;
  const withoutBase =
    beUrl && url.startsWith(beUrl) ? url.slice(beUrl.length) : url;
  const path = withoutBase.split('?')[0].replace(/^\/+/, '');
  const normalized = path
    .split('/')
    .map((segment) => (/^\d+$/.test(segment) ? ':id' : segment))
    .join('/')
    .toLowerCase();
  return normalized || 'unknown';
};

const truncate = (value: string | undefined) =>
  value?.slice(0, MAX_MESSAGE_LENGTH);

const getServerMessage = (data: unknown): string | undefined => {
  if (typeof data === 'string') return truncate(data);
  if (data && typeof data === 'object' && 'errors' in data) {
    const errors = (data as { errors?: { message?: string }[] }).errors;
    return truncate(errors?.[0]?.message);
  }
  return undefined;
};

export const buildApiFailureProperties = (error: ApiFailureError) => {
  const headers = error.config?.headers ?? {};
  const csrfHeader = headers['X-CSRF-TOKEN'] ?? headers['X-CSRF-TOKEN-LOCAL'];
  return {
    status: error.response?.status ?? 0,
    method: error.config?.method?.toUpperCase() ?? 'unknown',
    endpoint: normalizeEndpoint(error.config?.url),
    errorCode: error.code,
    errorMessage: truncate(error.message),
    serverMessage: getServerMessage(error.response?.data),
    csrfCookiePresent: !!getCsrfToken(),
    csrfHeaderSent: !!csrfHeader,
    csrfRetried: !!error.config?.__csrfRetried,
    visibleCookieBytes: document.cookie.length,
    online: navigator.onLine,
    page: window.location.pathname,
  };
};

const isCapReached = (now: number): boolean => {
  if (now - capWindowStart >= CAP_WINDOW_MS) {
    capWindowStart = now;
    eventsInCapWindow = 0;
    lastTrackedAt.forEach((trackedAt, key) => {
      if (now - trackedAt >= DEDUPE_WINDOW_MS) lastTrackedAt.delete(key);
    });
  }
  return eventsInCapWindow >= MAX_EVENTS_PER_CAP_WINDOW;
};

// Analytics must never change how the interceptor handles the original error.
export const trackApiFailure = (error: ApiFailureError) => {
  try {
    if (!shouldTrackApiFailure(error)) return;

    const analytics = AnalyticsContainer.analytics;
    if (!analytics) return;

    const now = Date.now();
    if (isCapReached(now)) return;

    const properties = buildApiFailureProperties(error);
    const { status: code, method, endpoint } = properties;
    const key = [code, method, endpoint].join('|');
    const previous = lastTrackedAt.get(key);
    if (previous !== undefined && now - previous < DEDUPE_WINDOW_MS) return;
    lastTrackedAt.set(key, now);
    eventsInCapWindow += 1;

    void Promise.resolve(analytics.track(API_FAILURE_EVENT, properties)).catch(
      () => undefined,
    );
  } catch {
    return;
  }
};

export const resetApiFailureTracking = () => {
  lastTrackedAt.clear();
  capWindowStart = 0;
  eventsInCapWindow = 0;
};
